import { useState, useEffect, useRef } from 'react';
import {
    Users, Package, Clock, CheckCircle, AlertCircle,
    XCircle, AlertTriangle, ShieldCheck, ChevronDown, ChevronUp,
    ChevronRight, RefreshCw, MapPin, TrendingUp, Activity, Star,
    UserCheck, ArrowUpRight, ArrowDownRight,
    Info, Store, Plug, Pause, Play
} from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    AreaChart, Area
} from 'recharts';
import { formatTL } from '../../utils/money';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addGoogleBaseLayer } from '../../utils/googleBaseLayer';
import { apiService } from '../../services/api.service';
import { fleetToast } from '../../utils/toast';
import { API_ENDPOINTS, FLEET_HUB_METHODS, FLEET_APP_EVENTS, config } from '../../config/environment';
import './DashboardPage.css';

const MAP_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#6366f1'];

/* ── Grafik renkleri ──────────────────────────────────────────────────────────
   Kategorik iki seri (Filo payı / Kurye kazancı) ve tek-ölçü çubukları için sabit
   slotlar. Değerler doğrulayıcıdan geçti (aydınlık yüzey #fcfcfb, koyu #1a1a19):
   renk körlüğü ayrımı ΔE 23.1 protan / 19.6 deutan, normal görüş ΔE 24.0 / 20.9.
   Aydınlık modda yeşilin yüzeyle kontrastı 2.74:1 → değerler DOĞRUDAN etiketle
   yazılır (kontrast uyarısının karşılığı budur), renk tek başına anlam taşımaz. */
const SERIES_LIGHT = { fleet: '#1baf7a', courier: '#2a78d6' };
const SERIES_DARK = { fleet: '#199e70', courier: '#3987e5' };

const isDarkTheme = () =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

const parseWkt = (wkt) => {
    if (!wkt) return [];
    const match = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
    if (!match) return [];
    return match[1].split(',').map(pair => {
        const [lng, lat] = pair.trim().split(/\s+/).map(Number);
        return { lat, lng };
    }).filter(p => !isNaN(p.lat) && !isNaN(p.lng));
};

/* Tooltip TÜM serileri gösterir: eskiden yalnız payload[0] yazıldığı için iki serili
   grafikte imlecin altındaki ikinci değer görünmüyordu. Metin ink renklerinde kalır,
   kimliği yanındaki renk noktası taşır. */
const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="fd-tt">
            {label != null && <div className="fd-tt-head">{label}</div>}
            {payload.map((p, i) => (
                <div key={i} className="fd-tt-row">
                    <span className="fd-tt-dot" style={{ background: p.color || p.payload?.color }} />
                    <span className="fd-tt-label">{p.name}</span>
                    <span className="fd-tt-val">{typeof p.value === 'number' ? formatTL(p.value) : p.value}</span>
                </div>
            ))}
        </div>
    );
};

/* Sayı taşıyan grafikler için tooltip: ChartTooltip değerleri TL biçimler, teslimat
   sayısında "12,00 ₺" yazardı. Aynı gövde, para biçimi yok. */
const CountTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="fd-tt">
            {label != null && <div className="fd-tt-head">{label}</div>}
            {payload.map((p, i) => (
                <div key={i} className="fd-tt-row">
                    <span className="fd-tt-dot" style={{ background: p.color || p.payload?.color }} />
                    <span className="fd-tt-label">{p.name}</span>
                    <span className="fd-tt-val">{p.value} teslimat</span>
                </div>
            ))}
        </div>
    );
};

/* Çubuk uçlarındaki doğrudan değer — her noktaya değil, 4 çubuğun her birine bir kez. */
const BarValueLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    return (
        <text x={x + width / 2} y={y - 7} textAnchor="middle" className="fd-bar-value">
            {formatTL(value)}
        </text>
    );
};

/**
 * showFinancials: parasal bloklar (filo kazancı, kurye ödemesi, kazanç grafikleri,
 * hakediş ve kurye performansı tabloları) YALNIZ filo sahibine çizilir. Backend bu
 * alanları sorumluya zaten boş döndürüyor; burada gizlenmeseydi sorumlu "0,00 TL"
 * dolu bir pano görürdü. Operasyonel kutular ve çalışma bölgeleri haritası kalır —
 * sorumlu kendi kapsamının istatistiklerini görebilmeli.
 */
const DashboardPage = ({ onNavigate, showFinancials = true }) => {
    /* Koyu mod ayrı SEÇİLMİŞ adımlar kullanır (aydınlık renklerin otomatik çevrimi değil);
       tema düğmesi data-theme'i değiştirdiğinde grafik renkleri de geçiş yapar. */
    const [SERIES, setSeries] = useState(() => (isDarkTheme() ? SERIES_DARK : SERIES_LIGHT));

    useEffect(() => {
        const apply = () => setSeries(isDarkTheme() ? SERIES_DARK : SERIES_LIGHT);
        apply();
        const mo = new MutationObserver(apply);
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => mo.disconnect();
    }, []);

    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [readinessLoading, setReadinessLoading] = useState(true);
    const [channelEnabled, setChannelEnabled] = useState(false);
    const [pendingStores, setPendingStores] = useState([]);
    const [storeBusy, setStoreBusy] = useState('');
    const [intakePausedAt, setIntakePausedAt] = useState(null);
    const [intakeBusy, setIntakeBusy] = useState(false);
    const [coverageAreas, setCoverageAreas] = useState([]);
    const [areasLoading, setAreasLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const mapContainerRef = useRef(null);
    const leafletMapRef = useRef(null);
    const layersRef = useRef([]);

    useEffect(() => { fetchDashboard(); fetchReadiness(); fetchCoverageAreas(); fetchChannel(); fetchPendingStores(); }, []);

    useEffect(() => {
        const handler = () => { fetchDashboard(); fetchReadiness(); };
        window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handler);
        window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`, handler);
        window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_MEMBER_UPDATE_NOTIFICATION}`, handler);
        // Genel filo bildirimi: kanal kararı ve mağaza onay talebi buradan düşüyor.
        const fleetNotice = () => { fetchChannel(); fetchPendingStores(); };
        window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_NOTIFICATION}`, fleetNotice);
        // Panelde yapılan onay/red push üretmez; özet kartları da tazelenmeli.
        window.addEventListener(FLEET_APP_EVENTS.JOIN_REQUESTS_CHANGED, handler);
        return () => {
            window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handler);
            window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`, handler);
            window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_MEMBER_UPDATE_NOTIFICATION}`, handler);
            window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_NOTIFICATION}`, fleetNotice);
            window.removeEventListener(FLEET_APP_EVENTS.JOIN_REQUESTS_CHANGED, handler);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; }
        };
    }, []);

    useEffect(() => {
        const container = mapContainerRef.current;
        if (!container) return;

        if (leafletMapRef.current && leafletMapRef.current.getContainer() !== container) {
            try { leafletMapRef.current.remove(); } catch {}
            leafletMapRef.current = null;
        }

        if (!leafletMapRef.current) {
            leafletMapRef.current = L.map(container, {
                center: [39.0, 35.5], zoom: 6, zoomControl: true, attributionControl: false
            });
            addGoogleBaseLayer(leafletMapRef.current);
        }

        const map = leafletMapRef.current;
        layersRef.current.forEach(l => l.remove());
        layersRef.current = [];

        const areasWithBoundary = coverageAreas.filter(a => a.boundaryType);
        const allBounds = [];

        areasWithBoundary.forEach((area, i) => {
            const color = MAP_COLORS[i % MAP_COLORS.length];
            const label = area.subRegionName || area.regionName || 'Bölge';
            if (area.boundaryType === 'Circle' && area.centerLatitude && area.centerLongitude && area.radiusMeters) {
                const circle = L.circle([area.centerLatitude, area.centerLongitude], {
                    radius: area.radiusMeters, color, weight: 2, opacity: 0.8, fillColor: color, fillOpacity: 0.2
                }).addTo(map);
                circle.bindTooltip(label, { permanent: true, direction: 'center', className: 'fd-map-label' });
                layersRef.current.push(circle);
                allBounds.push(circle.getBounds());
            }
            if (area.boundaryType === 'Polygon' && area.polygonWkt) {
                const points = parseWkt(area.polygonWkt);
                if (points.length >= 3) {
                    const latlngs = points.map(p => [p.lat, p.lng]);
                    const polygon = L.polygon(latlngs, {
                        color, weight: 2, opacity: 0.8, fillColor: color, fillOpacity: 0.2
                    }).addTo(map);
                    polygon.bindTooltip(label, { permanent: true, direction: 'center', className: 'fd-map-label' });
                    layersRef.current.push(polygon);
                    allBounds.push(polygon.getBounds());
                }
            }
        });

        if (allBounds.length > 0) {
            let combined = allBounds[0];
            for (let i = 1; i < allBounds.length; i++) combined = combined.extend(allBounds[i]);
            map.fitBounds(combined, { padding: [20, 20] });
        }
    }, [coverageAreas, loading]);

    const fetchDashboard = async () => {
        try { setLoading(true); setError(null); const r = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DASHBOARD); if (r.success) setDashboard(r.data); else setError(r.error || 'Dashboard verileri yüklenemedi'); } catch { setError('Dashboard verileri yüklenirken hata oluştu'); } finally { setLoading(false); }
    };
    const fetchReadiness = async () => {
        try { setReadinessLoading(true); const r = await apiService.get(API_ENDPOINTS.FLEET_READINESS.CHECK); if (r.success) setReadiness(r.data); } catch {} finally { setReadinessLoading(false); }
    };

    // Kanal rozeti filonun kendi kaydından okunuyor. Açılışta bir kez, sonra CRM kararı
    // SignalR ile düştüğünde tazeleniyor — sayfa yenilemeye gerek kalmasın.
    const fetchChannel = async () => {
        try {
            const r = await apiService.get(API_ENDPOINTS.FLEET_PANEL.ME);
            if (r.success) {
                setChannelEnabled(Boolean(r.data?.integrationChannelEnabled));
                setIntakePausedAt(r.data?.integrationIntakePausedAtUtc || null);
            }
        } catch { /* rozet kritik değil, sessiz geç */ }
    };

    // Yoğunlukta dış POS alımını durdur/aç. Hızlıyo restoranlarının siparişleri
    // etkilenmiyor — düğmenin yanındaki metin bunu açıkça söylüyor.
    const toggleIntake = async () => {
        const next = !intakePausedAt;
        setIntakeBusy(true);
        try {
            const r = await apiService.post(API_ENDPOINTS.FLEET_PANEL.INTEGRATION_INTAKE(next));

            /* apiService zarfı ZATEN açıyor: dönen nesne { success, data, message }.
               Burada ikinci kez `r.data.success` aranıyordu; bu uç `data: true` (düz bool)
               döndüğü için o alan hep undefined kalıyor ve sunucu 200 + success:true dese
               bile kullanıcı "İşlem tamamlanamadı." görüyordu. Aynı sayfadaki diğer tüm
               çağrılar `r.success` + `r.data` okuyor — tek sapma buydu. */
            if (r.success) {
                fleetToast.success(r.message || (next
                    ? 'Dış POS paket alımı duraklatıldı.'
                    : 'Dış POS paket alımı yeniden açıldı.'));
                await fetchChannel();
            } else {
                fleetToast.error(r.error || 'İşlem tamamlanamadı.');
            }
        } catch {
            fleetToast.error('İşlem tamamlanamadı.');
        } finally {
            setIntakeBusy(false);
        }
    };

    // Onay bekleyen entegratör mağazaları. Onaylanana kadar o restorandan sipariş
    // GELMEZ — bu yüzden anasayfada, göz kaçırmayacak yerde duruyor.
    const fetchPendingStores = async () => {
        try {
            const r = await apiService.get(API_ENDPOINTS.FLEET_PANEL.VENDOR_STORES(true));
            if (r.success) setPendingStores(Array.isArray(r.data) ? r.data : []);
        } catch { /* liste boş kalır, panelin geri kalanı çalışmaya devam eder */ }
    };

    const decideStore = async (storeId, approved) => {
        setStoreBusy(storeId);
        try {
            const r = await apiService.post(API_ENDPOINTS.FLEET_PANEL.VENDOR_STORE_DECISION(storeId, approved));
            if (r.success) {
                fleetToast.success(approved ? 'Mağaza onaylandı, sipariş alabilir.' : 'Mağaza reddedildi.');
                await fetchPendingStores();
            } else {
                // Hata mesajı `r.error`'da; `r.data` başarısızlıkta hiç dolmuyor.
                fleetToast.error(r.error || 'İşlem tamamlanamadı.');
            }
        } catch {
            fleetToast.error('İşlem tamamlanamadı.');
        } finally {
            setStoreBusy('');
        }
    };
    const fetchCoverageAreas = async () => {
        try { setAreasLoading(true); const r = await apiService.get(API_ENDPOINTS.FLEET_PANEL.COVERAGE_AREAS); if (r.success) setCoverageAreas(r.data || []); } catch {} finally { setAreasLoading(false); }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchDashboard(), fetchReadiness(), fetchCoverageAreas()]);
        setRefreshing(false);
    };

    const fmt = (v) => formatTL(v);
    const fmtInt = (v) => v == null ? '0' : new Intl.NumberFormat('tr-TR').format(v);
    const fmtDate = (v) => v ? new Date(v).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
    const fmtTime = (v) => v ? new Date(v).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '';

    const getConditionsList = () => {
        if (!readiness?.conditions) return [];
        const c = readiness.conditions;
        const list = [
            { ...c.fleetRecord, key: 'fleetRecord', infoText: 'Yönetici tarafından yönetilir' },
            { ...c.activeCourierMember, key: 'activeCourierMember', actionLabel: 'Kurye Ekle', navigateTo: 'members' },
            { ...c.legalAgreementAcceptance, key: 'legalAgreementAcceptance', actionLabel: 'Sözleşmeleri İncele', navigateTo: 'legal-agreements' },
            { ...c.activeCostHistory, key: 'activeCostHistory', infoText: 'Filo maliyetini CRM belirler' },
            { ...c.courierCostRecords, key: 'courierCostRecords', actionLabel: 'Maliyet Tanımla', navigateTo: 'courier-costs' },
        ].filter(item => item.conditionName);
        return list;
    };

    const d = dashboard || {};
    if (loading) return (<div className="fd-page"><div className="fd-loading"><div className="fd-spinner" /><span>Yükleniyor...</span></div></div>);

    const conditions = getConditionsList();
    const hasAreas = coverageAreas.some(a => a.boundaryType);
    const commissions = d.recentCommissions || [];
    const courierEarnings = d.courierEarnings || [];
    /* Sorumlu panosu: kurye başına PARASIZ teslimat sayıları. Backend bu listeyi yalnız
       sorumluya doldurur (sahip aynı sayıları kazanç tablosunda görüyor). */
    const courierActivity = d.courierActivity || [];

    const liveFleetShare = commissions.reduce((s, c) => s + (c.fleetShareAmount ?? 0), 0);
    const liveCourierTotal = commissions.reduce((s, c) => s + (c.courierEarning ?? 0), 0);
    const liveOrderCount = commissions.length;
    const activeCouriers = courierEarnings.filter(c => c.isActive).length;
    const busyCouriers = courierEarnings.filter(c => c.isBusy).length;
    const lastCommission = commissions[0] || null;

    const weekE = d.thisWeekEarnings ?? 0;
    const monthE = d.thisMonthEarnings ?? 0;
    const totalE = d.totalEarnings ?? 0;

    const weekDelta = weekE > 0 && monthE > 0 ? ((weekE / (monthE / 4)) * 100 - 100).toFixed(0) : null;

    /* Aynı ölçü (kazanç) farklı dönemlerde → TEK renk. Her çubuğa ayrı renk vermek
       kategori varmış gibi gösteriyordu (gökkuşağı); dönem kimliği zaten x ekseninde. */
    const earningsBarData = [
        { name: 'Anlık', val: liveFleetShare },
        { name: 'Hafta', val: weekE },
        { name: 'Ay', val: monthE },
        { name: 'Toplam', val: totalE },
    ];

    /* Anlık paket parası nasıl bölünüyor → bütünün parçaları. Halka (donut) yerine
       %100 yatay yığın: iki parça yan yana okunur, dilim içi etiket gerekmez. */
    const splitTotal = liveFleetShare + liveCourierTotal;
    const splitData = [
        { key: 'fleet', name: 'Filo payı', value: liveFleetShare, color: SERIES.fleet },
        { key: 'courier', name: 'Kurye kazancı', value: liveCourierTotal, color: SERIES.courier },
    ].filter(d => d.value > 0);

    const commissionFlow = commissions.slice().reverse().map(c => ({
        name: fmtTime(c.calculatedAt) || fmtDate(c.calculatedAt),
        fleet: c.fleetShareAmount ?? 0,
        courier: c.courierEarning ?? 0,
    }));

    const hourlyData = (() => {
        const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${String(i).padStart(2, '0')}:00`, count: 0, amount: 0 }));
        commissions.forEach(c => {
            if (c.calculatedAt) {
                const h = new Date(c.calculatedAt).getHours();
                hours[h].count += 1;
                hours[h].amount += (c.fleetShareAmount ?? 0);
            }
        });
        return hours;
    })();
    const maxHourCount = Math.max(...hourlyData.map(h => h.count), 1);

    /* Kanal kutuları: renk ve metin TEK yerde tanımlı, Raporlar sayfasıyla aynı renkler.
       Dış POS kutusunda tutar YOK — bedeli restoran filoya doğrudan ödüyor. */
    const emptyChannel = { totalDeliveries: 0, todayDeliveries: 0, activeDeliveries: 0, completedDeliveries: 0 };
    const channelTotal = (d.hizliyo?.totalDeliveries ?? 0) + (d.vendor?.totalDeliveries ?? 0);
    const channelBreakdown = [
        {
            key: 'hizliyo',
            label: 'Hızlıyo restoranları',
            color: '#22c55e',
            data: d.hizliyo || emptyChannel,
            foot: 'Ücretlendirme Hızlıyo üzerinden; kazanç kartlarına bu kanal girer.',
        },
        {
            key: 'vendor',
            label: 'Dış POS paketleri',
            color: '#0ea5e9',
            data: d.vendor || emptyChannel,
            foot: 'Paket bedeli restoranla aranızda; bu kanalda tutar raporlanmaz.',
        },
    ].map((c) => ({
        ...c,
        sharePct: channelTotal > 0 ? Math.round(((c.data.totalDeliveries ?? 0) / channelTotal) * 100) : 0,
    }));

    const dateStr = new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <div className="fd-page">
            {/* Üst bant TEK satır: başlık, taşınan kanallar ve durum aynı hizada.
                Kanal rozetleri ile "hazır" bandı ayrı bloklarken üç sıra yer kaplıyor,
                asıl iş (paket sayıları) ekranın altına iniyordu. Hazırlık TAMAMSA tek
                rozet yeter; eksikse aşağıdaki tam bölüm çizilir — orada yapılacak iş var. */}
            <div className="fd-header">
                <div className="fd-header-left">
                    <div className="fd-header-text">
                        <h1>Filo Kontrol Paneli</h1>
                        <p>{dateStr}</p>
                    </div>

                    <div className="fd-channels" aria-label="Taşınan paket kanalları">
                        <span className="fd-channel fd-channel-on">
                            <Store size={14} />
                            Hızlıyo restoran paketleri
                        </span>
                        {/* Üç durum var ve üçü farklı şey: kanala dahil değil (ticari, CRM verir),
                            dahil ama şu an almıyor (operasyonel, filo verir), alıyor. */}
                        <span className={`fd-channel ${
                            !channelEnabled ? 'fd-channel-off'
                                : intakePausedAt ? 'fd-channel-paused'
                                    : 'fd-channel-on'
                        }`}>
                            <Plug size={14} />
                            Dış POS paketleri
                            {!channelEnabled && <em>kapalı</em>}
                            {channelEnabled && intakePausedAt && <em>duraklatıldı</em>}
                        </span>

                        {channelEnabled && (
                            <button
                                type="button"
                                className={`fd-intake-btn ${intakePausedAt ? 'fd-intake-resume' : ''}`}
                                onClick={toggleIntake}
                                disabled={intakeBusy}
                                title={intakePausedAt
                                    ? 'Dış POS paket alımını yeniden aç'
                                    : 'Yoğunsanız dış POS paket alımını geçici olarak durdurun'}
                            >
                                {intakePausedAt ? <Play size={13} /> : <Pause size={13} />}
                                {intakePausedAt ? 'Alımı yeniden aç' : 'Yoğunum, almayı durdur'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="fd-header-actions">
                    {!readinessLoading && readiness?.isReady && (
                        <span className="fd-ready-pill" title="Filonuz sipariş almaya hazır">
                            <ShieldCheck size={13} />
                            Sipariş almaya hazır
                        </span>
                    )}
                    <div className="fd-header-live">
                        <div className="fd-live-dot" />
                        <span>Canlı</span>
                    </div>
                    <button className={`fd-refresh-btn${refreshing ? ' spinning' : ''}`} onClick={handleRefresh} disabled={refreshing}>
                        <RefreshCw size={15} />
                    </button>
                </div>
            </div>

            {error && (<div className="fd-alert fd-alert-error"><AlertCircle size={16} /><span>{error}</span></div>)}

            {/* Onay bekleyen mağazalar hazırlık bandının ÜSTÜNDE: onaylanmadıkça o
                restorandan sipariş gelmiyor, yani bu bekleyen bir iş değil kayıp ciro. */}
            {pendingStores.length > 0 && (
                <section className="fd-pending" aria-label="Onay bekleyen entegratör mağazaları">
                    <header className="fd-pending-head">
                        <Store size={16} />
                        <span className="fd-pending-title">
                            {pendingStores.length} mağaza onayınızı bekliyor
                        </span>
                        <span className="fd-pending-sub">
                            Onaylamadan bu restoranlardan sipariş gelmez
                        </span>
                    </header>

                    <ul className="fd-pending-list">
                        {pendingStores.map((s) => (
                            <li key={s.storeId} className="fd-pending-item">
                                <div className="fd-pending-info">
                                    <strong>{s.name}</strong>
                                    <span>
                                        {s.vendorName}, {s.subRegionName || s.addressText}
                                    </span>
                                </div>
                                <div className="fd-pending-actions">
                                    <button
                                        className="fd-pending-btn fd-pending-approve"
                                        onClick={() => decideStore(s.storeId, true)}
                                        disabled={storeBusy === s.storeId}
                                    >
                                        Onayla
                                    </button>
                                    <button
                                        className="fd-pending-btn fd-pending-reject"
                                        onClick={() => decideStore(s.storeId, false)}
                                        disabled={storeBusy === s.storeId}
                                    >
                                        Reddet
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {channelEnabled && intakePausedAt && (
                <p className="fd-intake-note">
                    Dış POS paket alımı duraklatıldı. <strong>Hızlıyo restoranlarının siparişleri
                    etkilenmiyor</strong> ve devam eden teslimatlar tamamlanıyor.
                </p>
            )}

            {/* Hazır durumu üst bantta tek rozet; burada YALNIZ eksik koşullar çizilir. */}
            {!readinessLoading && readiness && !readiness.isReady && (
                    <div className="fd-ready-section">
                        {/* Kart ızgarası yerine yapılacaklar listesi: yalnız EKSİK koşul satır olur ve
                            yanında ne yapılacağı durur. Tamamlananlar tek satırda anılır — beş
                            kutudan dördünün "tamam" demesi eksik olanı gözden kaçırıyordu. */}
                        <div className="fd-ready-header">
                            <div className="fd-ready-header-text">
                                <span className="fd-ready-title">Sipariş almak için eksik adımlar</span>
                                <span className="fd-ready-sub">
                                    {conditions.length} koşuldan {conditions.filter(c => c.isMet).length} tanesi tamam
                                </span>
                            </div>
                            <div className="fd-ready-progress-wrap">
                                <div className="fd-ready-progress">
                                    <div className="fd-ready-progress-fill" style={{ width: `${(conditions.filter(c => c.isMet).length / conditions.length) * 100}%` }} />
                                </div>
                            </div>
                        </div>
                        <ul className="fd-todo">
                            {conditions.filter(c => !c.isMet).map((c, i) => (
                                <li key={c.key || i} className="fd-todo-item">
                                    <div className="fd-todo-text">
                                        <strong>{c.conditionName}</strong>
                                        <span>{c.message || c.conditionName}</span>
                                    </div>
                                    {c.navigateTo ? (
                                        <button className="btn btn-primary btn-sm" onClick={() => onNavigate?.(c.navigateTo)}>
                                            {c.actionLabel}
                                        </button>
                                    ) : c.infoText ? (
                                        <span className="fd-todo-info">{c.infoText}</span>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                        {conditions.some(c => c.isMet) && (
                            <p className="fd-todo-done">
                                Tamamlananlar: {conditions.filter(c => c.isMet).map(c => c.conditionName).join(', ')}
                            </p>
                        )}
                    </div>
            )}

            {/* Ölçü kartları: TEK nötr yüzey. Eskiden her karta ayrı renk (yeşil/mavi/mor/amber)
                rayı + renkli ikon kutusu vardı — renk hiçbir şey anlatmıyordu, sadece süslüyordu
                ve "kategori var" izlenimi veriyordu. Şimdi renk yalnız ANLAM taşıdığı yerde:
                değişim yönü (artış/azalış) ve canlı durum noktası. Sıra: etiket → değer → alt bilgi. */}
            {showFinancials && (
            <div className="fd-kpi-row">
                <button
                    type="button"
                    className="fd-kpi fd-kpi-clickable"
                    onClick={() => onNavigate?.('earnings')}
                >
                    <div className="fd-kpi-head">
                        <span className="fd-kpi-label">Filo kazancı</span>
                    </div>
                    <div className="fd-kpi-val">{fmt(liveFleetShare)}</div>
                    <div className="fd-kpi-foot">
                        <span>{liveOrderCount} komisyon</span>
                        {lastCommission && (
                            <>
                                <span className="fd-kpi-sep" />
                                <span>son {fmt(lastCommission.fleetShareAmount ?? 0)}</span>
                            </>
                        )}
                    </div>
                </button>

                <div className="fd-kpi">
                    <div className="fd-kpi-head">
                        <span className="fd-kpi-label">Kurye ödemesi</span>
                    </div>
                    <div className="fd-kpi-val">{fmt(liveCourierTotal)}</div>
                    <div className="fd-kpi-foot">
                        <span>{activeCouriers} aktif</span>
                        <span className="fd-kpi-sep" />
                        <span>{busyCouriers} meşgul</span>
                    </div>
                </div>

                <div className="fd-kpi">
                    <div className="fd-kpi-head">
                        <span className="fd-kpi-label">Bu hafta</span>
                        {weekDelta && (
                            <span className={`fd-kpi-delta ${Number(weekDelta) >= 0 ? 'up' : 'down'}`}>
                                {Number(weekDelta) >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                                %{Math.abs(Number(weekDelta))}
                            </span>
                        )}
                    </div>
                    <div className="fd-kpi-val">{fmt(weekE)}</div>
                    <div className="fd-kpi-foot">
                        <span>geçen haftaya göre</span>
                        <span className="fd-kpi-sep" />
                        <span>aylık {fmt(monthE)}</span>
                    </div>
                </div>

                <div className="fd-kpi">
                    <div className="fd-kpi-head">
                        <span className="fd-kpi-label">Toplam kazanç</span>
                    </div>
                    <div className="fd-kpi-val">{fmt(totalE)}</div>
                    <div className="fd-kpi-foot">
                        <span>{fmtInt(d.totalCompletedDeliveries)} teslimat</span>
                    </div>
                </div>
            </div>
            )}

            <div className="fd-stats-row">
                <div className="fd-stat">
                    <div className="fd-stat-info">
                        <span className="fd-stat-val">{d.todayDeliveries ?? 0}</span>
                        <span className="fd-stat-label">Bugünkü paket</span>
                    </div>
                </div>
                <div className="fd-stat">
                    <div className="fd-stat-info">
                        <span className="fd-stat-val">{d.activeDeliveries ?? 0}</span>
                        <span className="fd-stat-label">Yoldaki paket</span>
                    </div>
                </div>
                <div className="fd-stat">
                    <div className="fd-stat-info">
                        <span className="fd-stat-val">{d.activeMembers ?? 0}<small>/{d.totalMembers ?? 0}</small></span>
                        <span className="fd-stat-label">Çalışan kurye</span>
                    </div>
                </div>
                <div className="fd-stat">
                    <div className="fd-stat-info">
                        <span className="fd-stat-val">{d.averageRating != null ? d.averageRating.toFixed(1) : '-'}</span>
                        <span className="fd-stat-label">Ortalama puan</span>
                    </div>
                </div>
                <div className="fd-stat">
                    <div className="fd-stat-info">
                        <span className="fd-stat-val">{d.pendingJoinRequests ?? 0}</span>
                        <span className="fd-stat-label">Bekleyen katılma talebi</span>
                    </div>
                </div>
            </div>

            {/* Kanal kırılımı: paket ADEDİ, para YOK. Dış POS paketinin bedeli restoranla
                filo arasında; panoda tutar göstermek kazanılmamış para raporlamak olurdu.
                İki kanal yan yana ve asla toplanmadan — üstteki kutular ikisinin toplamı. */}
            <div className="fd-card fd-channel-card">
                <div className="fd-card-head">
                    <span>Kanal dağılımı</span>
                    <span className="fd-channel-sub">Üstteki sayılar bu iki kanalın toplamı</span>
                </div>
                <div className="fd-card-body">
                    <div className="fd-channel-grid">
                        {channelBreakdown.map((c) => (
                            <div key={c.key} className="fd-channel-box" style={{ '--fd-ch': c.color }}>
                                <div className="fd-channel-box-head">
                                    <span className="fd-channel-box-name">{c.label}</span>
                                    <span className="fd-channel-box-total">{fmtInt(c.data.totalDeliveries)}</span>
                                </div>
                                <div className="fd-channel-meter">
                                    <i style={{ width: `${c.sharePct}%` }} />
                                </div>
                                <div className="fd-channel-metrics">
                                    <div><span>Bugün</span><b>{fmtInt(c.data.todayDeliveries)}</b></div>
                                    <div><span>Aktif</span><b>{fmtInt(c.data.activeDeliveries)}</b></div>
                                    <div><span>Tamamlanan</span><b>{fmtInt(c.data.completedDeliveries)}</b></div>
                                    <div><span>Pay</span><b>%{c.sharePct}</b></div>
                                </div>
                                <p className="fd-channel-foot">{c.foot}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bölge sorumlusunun kurye istatistiği: PARA YOK, yalnız teslimat sayısı.
                Sahipte bu blok çizilmez — onun karşılığı aşağıdaki kazanç tablolarıdır.
                Grafik sayfanın mevcut sistemini kullanır (aynı seri renkleri, aynı tooltip);
                sayılar çubuk uçlarında doğrudan yazılır, renk tek başına anlam taşımaz. */}
            {!showFinancials && courierActivity.length > 0 && (
                <div className="fd-card">
                    <div className="fd-card-head">
                        <Users size={16} />
                        <span>Bölgemdeki Kurye İstatistikleri</span>
                        <span className="fd-badge-count">{courierActivity.length}</span>
                    </div>
                    <div className="fd-card-body">
                        <div style={{ width: '100%', height: Math.max(180, courierActivity.length * 46) }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={courierActivity.map(c => ({
                                        name: c.courierName,
                                        bugun: c.todayDeliveries,
                                        hafta: c.thisWeekDeliveries,
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: 28, bottom: 4, left: 8 }}
                                    barCategoryGap={12}
                                >
                                    <CartesianGrid horizontal={false} strokeDasharray="3 3" className="fd-grid" />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                                    <Tooltip content={<CountTooltip />} cursor={{ fillOpacity: 0.06 }} />
                                    <Bar dataKey="hafta" name="Bu hafta" fill={SERIES.courier} radius={[0, 3, 3, 0]} barSize={11} />
                                    <Bar dataKey="bugun" name="Bugün" fill={SERIES.fleet} radius={[0, 3, 3, 0]} barSize={11} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="fd-legend">
                            <span><span className="fd-legend-dot" style={{ background: SERIES.courier }} />Bu hafta</span>
                            <span><span className="fd-legend-dot" style={{ background: SERIES.fleet }} />Bugün</span>
                        </div>
                        <div className="fd-table-wrap">
                            <table className="fd-table fd-table-compact">
                                <thead>
                                    <tr>
                                        <th>Kurye</th>
                                        <th>Durum</th>
                                        <th className="r">Bugün</th>
                                        <th className="r">Bu hafta</th>
                                        <th className="r">Toplam</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {courierActivity.map((c) => (
                                        <tr key={c.courierId}>
                                            <td>{c.courierName}</td>
                                            <td>
                                                <span className={`fd-pill ${c.isBusy ? 'busy' : c.isActive ? 'on' : 'off'}`}>
                                                    {c.isBusy ? 'Meşgul' : c.isActive ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td className="r"><span className="fd-cell-mono">{c.todayDeliveries}</span></td>
                                            <td className="r"><span className="fd-cell-mono">{c.thisWeekDeliveries}</span></td>
                                            <td className="r"><span className="fd-cell-mono">{c.totalDeliveries}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showFinancials && (
            <div className="fd-card">
                <div className="fd-card-head">
                    <TrendingUp size={16} />
                    <span>Kazanç Karşılaştırması</span>
                </div>
                <div className="fd-card-body">
                    <div className="fd-compare-row">
                        <div className="fd-compare-bar">
                            {/* Tek seri → gösterge (legend) yok, başlık ölçüyü söylüyor. Değerler
                                çubuk uçlarında doğrudan yazılı; y ekseni bu yüzden kaldırıldı. */}
                            <ResponsiveContainer width="100%" height={210}>
                                <BarChart data={earningsBarData} barSize={26} margin={{ top: 22, right: 8, bottom: 0, left: 8 }}>
                                    <CartesianGrid stroke="var(--border-color)" strokeOpacity={0.45} vertical={false} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                                        axisLine={false}
                                        tickLine={false}
                                        dy={4}
                                    />
                                    <YAxis hide />
                                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--hover-light)' }} />
                                    <Bar
                                        dataKey="val"
                                        name="Kazanç"
                                        fill={SERIES.fleet}
                                        radius={[4, 4, 0, 0]}
                                        label={<BarValueLabel />}
                                        isAnimationActive={false}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="fd-compare-split">
                            <div className="fd-split-title">Anlık paket dağılımı</div>
                            {splitData.length > 0 ? (
                                <>
                                    {/* Bütünün parçaları: %100 yatay yığın. Parçalar arasında 2px
                                        yüzey boşluğu var (renk körlüğünde de sınır görünür). */}
                                    <div className="fd-split-bar">
                                        {splitData.map((d) => (
                                            <div
                                                key={d.key}
                                                className="fd-split-seg"
                                                style={{
                                                    flexGrow: d.value,
                                                    background: d.color
                                                }}
                                                title={`${d.name}: ${fmt(d.value)}`}
                                            />
                                        ))}
                                    </div>
                                    <div className="fd-split-legend">
                                        {splitData.map((d) => (
                                            <div key={d.key} className="fd-split-legend-item">
                                                <span className="fd-legend-dot" style={{ background: d.color }} />
                                                <span className="fd-split-legend-label">{d.name}</span>
                                                <span className="fd-split-legend-val">
                                                    {fmt(d.value)}
                                                    <small>
                                                        {splitTotal > 0 ? `, %${Math.round((d.value / splitTotal) * 100)}` : ''}
                                                    </small>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="fd-empty fd-empty-sm"><TrendingUp size={22} /><span>Veri yok</span></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            )}

            {showFinancials && commissionFlow.length > 1 && (
                <div className="fd-card">
                    <div className="fd-card-head">
                        <TrendingUp size={16} />
                        <span>Kazanç Akışı</span>
                    </div>
                    <div className="fd-card-body">
                        {/* Filo payı + kurye kazancı paket bedelinin PARÇALARI → YIĞIN alan.
                            Eskiden iki dolgu üst üste binip birbirini gizliyordu; yığında
                            toplam da okunur. Segmentler arası 2px yüzey boşluğu bırakılır. */}
                        <ResponsiveContainer width="100%" height={190}>
                            <AreaChart data={commissionFlow} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
                                <CartesianGrid stroke="var(--border-color)" strokeOpacity={0.45} vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 10, fill: 'var(--text-secondary)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    minTickGap={28}
                                    dy={4}
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: 'var(--text-secondary)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={44}
                                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}b` : v)}
                                />
                                <Tooltip
                                    content={<ChartTooltip />}
                                    cursor={{ stroke: 'var(--text-tertiary)', strokeWidth: 1, strokeDasharray: '4 4' }}
                                />
                                <Area
                                    type="monotone"
                                    stackId="earnings"
                                    dataKey="fleet"
                                    name="Filo payı"
                                    stroke={SERIES.fleet}
                                    strokeWidth={2}
                                    fill={SERIES.fleet}
                                    fillOpacity={0.22}
                                    dot={false}
                                    activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-primary)' }}
                                    isAnimationActive={false}
                                />
                                <Area
                                    type="monotone"
                                    stackId="earnings"
                                    dataKey="courier"
                                    name="Kurye kazancı"
                                    stroke={SERIES.courier}
                                    strokeWidth={2}
                                    fill={SERIES.courier}
                                    fillOpacity={0.22}
                                    dot={false}
                                    activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-primary)' }}
                                    isAnimationActive={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                        <div className="fd-legend">
                            <span><span className="fd-legend-dot" style={{ background: SERIES.fleet }} />Filo payı</span>
                            <span><span className="fd-legend-dot" style={{ background: SERIES.courier }} />Kurye kazancı</span>
                        </div>
                    </div>
                </div>
            )}

            {showFinancials && (
            <div className="fd-card">
                <div className="fd-card-head">
                    <Clock size={16} />
                    <span>Saatlik Dağılım</span>
                </div>
                <div className="fd-card-body">
                    {commissions.length > 0 ? (
                        <div>
                            <div className="fd-heatmap-grid">
                                {hourlyData.map((h) => {
                                    const intensity = h.count / maxHourCount;
                                    return (
                                        <div key={h.hour} className="fd-heatmap-cell-wrap">
                                            <div
                                                className="fd-heatmap-cell"
                                                style={{
                                                    backgroundColor: h.count === 0
                                                        ? 'var(--fd-heatmap-empty, #1f1f23)'
                                                        : `rgba(34, 197, 94, ${0.15 + intensity * 0.85})`,
                                                    fontSize: h.count > 0 ? 12 : 10,
                                                    fontWeight: h.count > 0 ? 700 : 400,
                                                    color: h.count === 0
                                                        ? 'var(--text-tertiary)'
                                                        : intensity > 0.5 ? '#fff' : '#16a34a',
                                                }}
                                                title={`${h.hour}: ${h.count} teslimat, ${fmt(h.amount)}`}
                                            >
                                                {h.count > 0 ? h.count : '·'}
                                            </div>
                                            <span className="fd-heatmap-label">{h.hour.slice(0, 2)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="fd-heatmap-legend">
                                <span>Az</span>
                                {[0.15, 0.35, 0.55, 0.75, 1].map((op, i) => (
                                    <div key={i} className="fd-heatmap-legend-box" style={{ backgroundColor: `rgba(34, 197, 94, ${op})` }} />
                                ))}
                                <span>Çok</span>
                            </div>
                        </div>
                    ) : (
                        <div className="fd-empty fd-empty-sm"><Clock size={22} /><span>Henüz teslimat verisi yok</span></div>
                    )}
                </div>
            </div>
            )}
            {showFinancials && (

            <div className="fd-card">
                <div className="fd-card-head">
                    <div className="fd-card-head-left">
                        <div className="fd-live-dot" />
                        <span>Son Hakedişler</span>
                        <span className="fd-badge-count">{commissions.length}</span>
                    </div>
                    <button className="fd-link-btn" onClick={() => onNavigate?.('earnings')}>Tümünü Gör <ChevronRight size={14} /></button>
                </div>
                {commissions.length > 0 ? (
                    <div className="fd-table-wrap">
                        <table className="fd-table">
                            <thead>
                                <tr>
                                    <th>Tarih</th>
                                    <th>Partner</th>
                                    <th>Kurye</th>
                                    <th className="r">Kurye Maliyeti</th>
                                    <th className="r">Filo Payı</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commissions.map((c, i) => (
                                    <tr key={i} className={i === 0 ? 'fd-row-highlight' : ''}>
                                        <td><span className="fd-cell-muted">{fmtDate(c.calculatedAt)}</span></td>
                                        <td><span className="fd-cell-name">{c.partnerName || '-'}</span></td>
                                        <td><span className="fd-cell-name">{c.courierName || '-'}</span></td>
                                        <td className="r"><span className="fd-cell-mono">{fmt(c.courierEarning)}</span></td>
                                        <td className="r"><span className={`fd-cell-mono${c.fleetShareAmount > 0 ? ' green' : ''}`}>{fmt(c.fleetShareAmount)}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="fd-empty"><TrendingUp size={28} /><span>Henüz hakediş kaydı yok</span></div>
                )}
            </div>
            )}

            <div className="fd-grid-2">
                {showFinancials && (
                <div className="fd-card">
                    <div className="fd-card-head">
                        <Users size={16} />
                        <span>Kurye Performansı</span>
                        <span className="fd-badge-count">{courierEarnings.length}</span>
                    </div>
                    {courierEarnings.length > 0 ? (
                        <div className="fd-table-wrap">
                            <table className="fd-table fd-table-compact">
                                <thead>
                                    <tr>
                                        <th>Kurye</th>
                                        <th>Durum</th>
                                        <th className="r">Bugün</th>
                                        <th className="r">Hafta</th>
                                        <th className="r">Toplam</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {courierEarnings.map((c, i) => (
                                        <tr key={i}>
                                            <td>
                                                <div className="fd-courier-cell">
                                                    <span className={`fd-dot ${c.isActive ? (c.isBusy ? 'busy' : 'active') : 'off'}`} />
                                                    <span className="fd-cell-name">{c.courierName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`fd-status-pill ${c.isActive ? (c.isBusy ? 'busy' : 'active') : 'off'}`}>
                                                    {c.isActive ? (c.isBusy ? 'Meşgul' : 'Aktif') : 'Çevrimdışı'}
                                                </span>
                                            </td>
                                            <td className="r">
                                                <span className={`fd-cell-mono${c.todayEarnings > 0 ? ' green' : ''}`}>{fmt(c.todayEarnings)}</span>
                                                <span className="fd-cell-sub">{c.todayDeliveries} teslimat</span>
                                            </td>
                                            <td className="r">
                                                <span className="fd-cell-mono">{fmt(c.thisWeekEarnings)}</span>
                                                <span className="fd-cell-sub">{c.thisWeekDeliveries} teslimat</span>
                                            </td>
                                            <td className="r">
                                                <span className="fd-cell-mono">{fmt(c.totalEarnings)}</span>
                                                <span className="fd-cell-sub">{c.totalDeliveries} teslimat</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="fd-empty"><Users size={28} /><span>Henüz kurye kazanç verisi yok</span></div>
                    )}
                </div>
                )}

                <div className="fd-card">
                    <div className="fd-card-head">
                        <MapPin size={16} />
                        <span>Çalışma Bölgeleri</span>
                        {hasAreas && <span className="fd-badge-count">{coverageAreas.filter(a => a.boundaryType).length}</span>}
                    </div>
                    <div className="fd-map-wrap">
                        <div className="fd-map" ref={mapContainerRef} />
                        {areasLoading && <div className="fd-map-loading"><div className="fd-spinner" /></div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
